import { useState } from 'react'
import type { MouseEvent } from 'react'
import { TruckShippingIcon } from 'src/components/Icons/TruckShippingIcon'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { Modal } from 'src/components/restructure/common/Modal'
import { ShippingConditionsModal } from 'src/components/restructure/product/sections/ShippingConditionsModal'
import { useMobile } from 'src/hooks/useMobile'
import type { ShippingCampaignEvaluation } from 'src/utils/shippingCampaign'

type ShippingCampaignFlagProps = {
  isEligible: boolean
  ctaLabel: string
  ctaActionText: string
  badgeSubtext?: string
  modal: ShippingCampaignEvaluation['modal']
}

export function ShippingCampaignFlag({
  isEligible,
  ctaLabel,
  ctaActionText,
  badgeSubtext,
  modal,
}: Readonly<ShippingCampaignFlagProps>) {
  const { isMobile } = useMobile()
  const { openModal, closeModal } = useModalSheet()
  const [isOpen, setIsOpen] = useState(false)

  const handleOpenShippingConditions = (
    event: MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
    event.stopPropagation()

    if (isMobile) {
      openModal({
        content: (
          <ShippingConditionsModal
            title={modal.title}
            subtitle={modal.subtitle}
            rules={modal.rules}
            footer={modal.footer}
            onClose={closeModal}
          />
        ),
        height: 'auto',
        width: '375px',
        header: {
          isVisible: false,
          component: undefined,
        },
      })

      return
    }

    setIsOpen(true)
  }

  return (
    <>
      <div className="mt-2 flex items-center gap-2 md:mt-3">
        <span>
          <TruckShippingIcon
            color="#00875A"
            size={20}
            className="inline-block animate-truck-float"
          />
        </span>

        <div className="flex flex-col">
          <div className="flex flex-col md:flex-row md:items-center md:gap-1">
            <span
              className={`text-[10px] md:text-xs font-semibold leading-none tracking-normal md:tracking-[0.04em] uppercase ${
                isEligible ? 'text-shippingCampaignSuccess' : 'text-primaryBlue'
              }`}
            >
              {ctaLabel}
            </span>

            {badgeSubtext ? (
              <span className="text-[10px] font-normal leading-[1.4] text-restructure-primary">
                {badgeSubtext}
              </span>
            ) : null}
          </div>

          <button
            type="button"
            className="w-fit cursor-pointer border-0 bg-transparent p-0 text-left font-inter text-[10px] font-normal leading-[1.4] text-shippingCampaignSupport transition-colors duration-150 hover:text-darkGray"
            onClick={handleOpenShippingConditions}
          >
            {ctaActionText}
          </button>
        </div>
      </div>

      {!isMobile && (
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          className="w-[calc(100%-2rem)] max-w-[425px] restructure-small-desktop:w-full restructure-small-desktop:rounded-lg"
          opacityTailwind="opacity-40"
          dataTestid="shippingCampaignModal"
        >
          <ShippingConditionsModal
            title={modal.title}
            subtitle={modal.subtitle}
            rules={modal.rules}
            footer={modal.footer}
            onClose={() => setIsOpen(false)}
          />
        </Modal>
      )}
    </>
  )
}
